import { Tooltip } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { toastError, toastSuccess } from '@components/Toast';

import { fetchProductInfo } from '@store/actions/DesignToolAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import ApiClient from '@lib/apiClient';

import { changeDateFormat, getShortName, onGetLocalFromUtc } from '@utils/Util';

import { BASE_URL } from '@/constants/constant';

const FitType = ({
	item,
	onUpdateData,
	onImageUpload,
	onRemove,
	originFrom,
	onSetActiveTool,
	onPlaceImageOnArtboard,
	index,
	entityType = 'PRODUCT'
}) => {
	const [isAdded, setIsAdded] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const { id } = useParams();
	const dispatch = useDispatch();
	const [remarks, setRemarks] = useState('');

	useEffect(() => {
		setRemarks(item?.remarks || '');
	}, [item]);

	function openFileUploader() {
		const fileInput = document.getElementById(`fileInput_${item?.id}`);
		fileInput.click();
	}

	// Will download the .dxf file including missing MIME type.
	const downloadDxfFile = async (docUrl, fileName) => {
		try {
			const response = await fetch(docUrl);

			const blob = await response.blob();

			const blobUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = blobUrl;
			link.download = fileName;
			link.style.display = 'none';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			// Clean up blob URL
			window.URL.revokeObjectURL(blobUrl);
		} catch (error) {
			toastError('Error downloading the DXF file:', error);
		}
	};

	async function downloadFile(docUrl) {
		try {
			const lastSlashIndex = docUrl.lastIndexOf('/');
			const baseUrl = docUrl.substring(0, lastSlashIndex + 1);
			const filename = docUrl.substring(lastSlashIndex + 1);

			const encodedFilename = filename.replaceAll('#', '%23');
			const encodedUrl = baseUrl + encodedFilename;

			if (filename.toLowerCase().includes('.dxf')) {
				await downloadDxfFile(encodedUrl, filename);
				return;
			}

			const link = document.createElement('a');
			link.href = encodedUrl;
			link.download = filename;
			link.target = '_blank';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (error) {
			toastError('Error downloading the file:', error);
		}
	}

	const onAddTechPack = async (itemId) => {
		try {
			setIsLoading(true);
			const { data } = await ApiClient.POST('uploadProductFile', {}, `/${itemId}/product/${id}/copy`);
			if (data.message) {
				setIsLoading(false);
				setIsAdded(true);
			}
		} catch (error) {
			setIsLoading(false);
			if (error.message) {
				toastError(error.message);
			} else {
				toastError('Something went wrong! Please try again.');
			}
		}
	};

	const onConvertAsImage = async (file) => {
		if (file?.documentResponse?.name.includes('.pdf')) {
			dispatch(openLoader());
			try {
				const { data } = await ApiClient.POST(
					`${BASE_URL}/entity-file/${file?.documentResponse?.id}/product/${id}/convert-to-image`,
					file,
					'',
					true
				);

				if (data) {
					toastSuccess('File converted successfully!');
					onSetActiveTool('images');
					dispatch(fetchProductInfo(id));
				}
			} catch (response) {
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			} finally {
				dispatch(closeLoader());
			}
		}
	};

	const getLink = (item) => {
		let link = item?.isLink ? item?.link : item?.documentResponse?.name;
		return (
			<>
				<Tooltip title={link} placement="top" arrow>
					<span className="regular-14  gray_dark_01 weight-500 hover-underline cursor-pointer">
						{item?.isLink ? (
							<>
								<a href={item?.link} target="_blank" rel="noopener noreferrer">
									{getShortName(link, entityType === 'PRODUCT' ? 18 : 100)}
								</a>
								{item?.isLink && (
									<img src={`${process.env.PUBLIC_URL}/icons/arrow_forward_dark.svg`} />
								)}
							</>
						) : (
							<>
								<span onClick={() => downloadFile(item?.documentResponse?.docUrl)}>
									{getShortName(link, entityType === 'PRODUCT' ? 17 : 100)}
								</span>
							</>
						)}
					</span>
				</Tooltip>
			</>
		);
	};

	return (
		<div className="single-type-file">
			<div className="file-type-content-box">
				<div className="d-flex justify-content-between">
					<span className="regular-12 green weight-500"> {item?.isFinal && '# FINAL'}</span>
					<div className="more-dropdown dropdown">
						<button
							className="button text-only"
							type="button"
							id={`moreInfo_${item?.id}`}
							data-toggle="dropdown"
							aria-expanded="false"
						>
							<img
								src={`${process.env.PUBLIC_URL}/icons/more_icon.svg`}
								alt="more"
								className="mr-1"
							/>
						</button>
						<ul
							className="dropdown-menu dropdown-menu-right shadow-lg"
							role="menu"
							aria-labelledby="moreInfo"
							x-placement="bottom-end"
						>
							{' '}
							<>
								{!item?.isFinal && (
									<li onClick={() => onUpdateData('final')}>
										<span>Mark as final version </span>
									</li>
								)}
								<li onClick={openFileUploader}>
									<span>Upload new File</span>
								</li>
								{entityType === 'PRODUCT' &&
									item?.documentResponse?.name.includes('.pdf') && (
										<li onClick={() => onConvertAsImage(item)}>
											<span>Convert as image</span>
										</li>
									)}
								{entityType === 'PRODUCT' &&
									item?.documentResponse?.name.includes('.pdf') && (
										<li onClick={() => onPlaceImageOnArtboard(item)}>
											<span>Place in the artboard</span>
										</li>
									)}

								<input
									type="file"
									id={`fileInput_${item?.id}`}
									style={{ display: 'none' }}
									onChange={onImageUpload}
									accept={
										entityType === 'PRODUCT'
											? '*/*'
											: '.pdf, .xls, .xlsx, .xlsb, .doc, .docx, .psd, .ai, .dxf, .zip, .eps'
									}
									multiple
								/>
							</>
							<li onClick={() => downloadFile(item?.documentResponse?.docUrl)}>
								<span>Download</span>
							</li>
							{!item?.deletedAt && !item?.deletedBy && (
								<li onClick={onRemove}>
									<span className="red">Delete</span>
								</li>
							)}
						</ul>
					</div>
				</div>
				<div className="version-info d-flex justify-content-between">
					<ul className="common-list-component">
						<li>
							<span className="regular-14  gray_dark_01 weight-500">{index}</span>
						</li>
						<li>{getLink(item)}</li>
					</ul>
				</div>
				<ul className="common-list-component mt-1 mb-12">
					<li>
						<span>{item?.addedBy?.nickName} </span>
					</li>
					<li>
						<span>
							{changeDateFormat(
								onGetLocalFromUtc(item?.addedAt, ''),
								'YYYY-MM-DD HH:mm:ss',
								'DD MMM, YYYY, h:mm a'
							).toUpperCase()}
						</span>
					</li>
				</ul>

				{originFrom !== 'FILE' &&
					(!isAdded ? (
						<div className="add-techpack">
							<button
								className="button outline size36"
								disabled={isLoading}
								onClick={() => onAddTechPack(item.id)}
							>
								<div className="d-flex align-items-center ">
									<img
										src={`${process.env.PUBLIC_URL}/icons/left-arrwo-dark.svg`}
										alt="arrow"
									/>
									<span>Add this techpack</span>
								</div>
							</button>
						</div>
					) : (
						<div className="add-techpack">
							<div className="d-flex align-items-center justify-content-center">
								<img src={`${process.env.PUBLIC_URL}/icons/shiped.svg`} alt="arrow" />
								<span className="green regular-14 ml-1">Added</span>
							</div>
						</div>
					))}
				<div className="remarks">
					<div className="form-group mb-0">
						<textarea
							value={remarks}
							placeholder="Remarks"
							onBlur={(e) => onUpdateData('remarks', e.target.value)}
							onChange={(e) => setRemarks(e.target.value)}
							onInput={(e) => {
								e.target.style.height = 'auto';
								e.target.style.height = `${Math.max(e.target.scrollHeight, e.target.offsetHeight)}px`;
								if (!e.target.value) {
									e.target.style.height = '34px';
								}
							}}
						/>
					</div>
				</div>
				{item?.deletedBy && (
					<Tooltip title={item?.deletedBy?.name} arrow placement="top">
						<span className="regular-12 red">
							Deleted by: {item?.deletedBy?.nickName} ,{' '}
							{changeDateFormat(
								onGetLocalFromUtc(item?.deletedAt, ''),
								'YYYY-MM-DD HH:mm:ss',
								'DD MMM YYYY, h:mm a'
							).toUpperCase()}
						</span>
					</Tooltip>
				)}
			</div>
		</div>
	);
};

export default FitType;
